<template>
  <section class="hexagon-explainer-module-component">
    <img
      ref="parallaxBG"
      class="parallax-img"
      src="@/assets/img/bg-helix-exp3.png"
      role="presentation"
      alt="" />
    <div class="container">
      <div
        ref="shapeContainer"
        class="shape-container"
        :class="open ? 'active' : ''">
        <HexagonShape
          :data="hexagonData"
          @active-hex="(val) => setActiveHex(val)" />
      </div>
      <div class="row-svg"></div>
      <div
        class="row"
        :class="activeHex !== '' ? 'active' : ''">
        <Transition name="slide-fade">
          <div v-if="activeHex !== ''">
            <div class="top-area">
              <div class="icon">
                <Transition
                  @before-enter="beforeEnter"
                  @enter="enter"
                  @leave="leave">
                  <img
                    v-if="!main"
                    src="@/assets/img/arrow-button.svg"
                    alt=""
                    @click="main = true" />
                </Transition>
              </div>
              <div class="close-button">
                <img
                  src="@/assets/img/close.svg"
                  alt=""
                  @click="collapse()" />
              </div>
            </div>

            <HexagonContent
              :data="contentData()"
              @setmain="(val) => (main = val)" />
          </div>
        </Transition>
      </div>
    </div>
  </section>
</template>

<script setup>
/* #META
   {
    "NAME": "HexagonExplainerComponent",
    "CLASSNAME": "hexagon-explainer-module-component",
    "URL": "http://insitro.local/test/"
   }
  #ENDMETA */

/*
 *   Insert your logic here
 */
import { computed, ref, watch, onMounted, onBeforeUnmount } from 'vue'
import HexagonShape from './HexagonShape.vue'
import HexagonContent from './HexagonContent.vue'
import gsap from 'gsap'
import ScrollTrigger from 'gsap/ScrollTrigger'
import useUtils from '@/components/composables/useUtils.js'

gsap.registerPlugin(ScrollTrigger)
const props = defineProps(['data'])
const { device } = useUtils()

const activeHex = ref('')
const open = ref(false)
const main = ref(true)
const shapeContainer = ref(null)
const parallaxBG = ref(null)

const parallaxImg = () => {
  gsap.fromTo(
    parallaxBG.value,
    {
      y: -150,
    },
    {
      scrollTrigger: {
        id: 'hexagon-shape',
        trigger: parallaxBG.value,
        start: 'top center',
        end: 'bottom center',
        scrub: true,
      },
      y: 50,
    }
  )
}

onMounted(() => {
  parallaxImg()
})

onBeforeUnmount(() => {
  const parallaxAnim = ScrollTrigger.getById('hexagon-shape')
  if (parallaxAnim) parallaxAnim.kill()
})

const setActiveHex = (val) => {
  activeHex.value = val
  main.value = true
}

watch(activeHex, (newValue) => {
  if (device.value !== 'desktop') {
    if (open.value === false && newValue !== '') {
      open.value = true
    } else if (open.value === false && newValue === '') {
      open.value = true
    }
  } else {
    if (open.value === false && newValue !== '') {
      gsap.to(shapeContainer.value, {
        x: '-26%',
        duration: 0.5,
        ease: 'power4.out',
        onStart: () => {
          open.value = true
        },
      })
    } else if (open.value === false && newValue === '') {
      gsap.to(shapeContainer.value, {
        x: '2%',
        duration: 0.5,
        ease: 'power4.out',
        delay: 0.1,
      })
    }
  }
})

const contentData = () => {
  if (activeHex.value === 'one') {
    return {
      title: props.data.hexagons.hexagon_1_properties.title,
      description:
        props.data.hexagons.hexagon_1_properties.hexagon_data.description,
      subItems: props.data.hexagons.hexagon_1_properties.hexagon_data.sub_items,
      mini_icon: props.data.hexagons.hexagon_1_properties.mini_icon,
      mainScreen: main.value,
    }
  } else if (activeHex.value === 'two') {
    return {
      title: props.data.hexagons.hexagon_2_properties.title,
      description:
        props.data.hexagons.hexagon_2_properties.hexagon_data.description,
      subItems: props.data.hexagons.hexagon_2_properties.hexagon_data.sub_items,
      mini_icon: props.data.hexagons.hexagon_2_properties.mini_icon,
      mainScreen: main.value,
    }
  } else if (activeHex.value === 'three') {
    return {
      title: props.data.hexagons.hexagon_3_properties.title,
      description:
        props.data.hexagons.hexagon_3_properties.hexagon_data.description,
      subItems: props.data.hexagons.hexagon_3_properties.hexagon_data.sub_items,
      mini_icon: props.data.hexagons.hexagon_3_properties.mini_icon,
      mainScreen: main.value,
    }
  } else if (activeHex.value === 'four') {
    return {
      title: props.data.hexagons.hexagon_4_properties.title,
      description:
        props.data.hexagons.hexagon_4_properties.hexagon_data.description,
      subItems: props.data.hexagons.hexagon_4_properties.hexagon_data.sub_items,
      mini_icon: props.data.hexagons.hexagon_4_properties.mini_icon,
      mainScreen: main.value,
    }
  } else {
    return {
      title: '',
      description: '',
      subItems: [],
      mainScreen: main.value,
    }
  }
}

const collapse = () => {
  activeHex.value = ''
  open.value = false
}

const hexagonData = computed(() => {
  return {
    one: {
      title: props.data.hexagons.hexagon_1_properties.title,
      bg_image: props.data.hexagons.hexagon_1_properties.background_image,
      icon: props.data.hexagons.hexagon_1_properties.icon,
    },
    two: {
      title: props.data.hexagons.hexagon_2_properties.title,
      bg_image: props.data.hexagons.hexagon_2_properties.background_image,
      icon: props.data.hexagons.hexagon_2_properties.icon,
    },
    three: {
      title: props.data.hexagons.hexagon_3_properties.title,
      bg_image: props.data.hexagons.hexagon_3_properties.background_image,
      icon: props.data.hexagons.hexagon_3_properties.icon,
    },
    four: {
      title: props.data.hexagons.hexagon_4_properties.title,
      bg_image: props.data.hexagons.hexagon_4_properties.background_image,
      icon: props.data.hexagons.hexagon_4_properties.icon,
    },
    config: {
      activeHex: activeHex.value,
    },
  }
})

// Animation hooks

const beforeEnter = (el) => {
  gsap.set(el, {
    scale: 0,
    rotate: 180,
  })
}

const enter = (el, done) => {
  gsap.to(el, {
    scale: 1,
    duration: 0.5,
    ease: 'power2.inOut',
    onComplete: function () {
      gsap.set(this.targets(), { clearProps: 'all' })
      done()
    },
  })
}

const leave = (el, done) => {
  gsap.to(el, {
    scale: 0,
    duration: 0.3,
    ease: 'power2.inOut',
    onComplete: function () {
      gsap.set(this.targets(), { clearProps: 'all' })
      done()
    },
  })
}
</script>

<style lang="scss" scoped>
.hexagon-explainer-module-component {
  @apply relative max-w-full;
  .parallax-img {
    @apply object-cover w-full h-full absolute;
  }
  .container {
    min-height: 800px;
    @apply flex flex-col lg:flex-row my-10 relative h-auto lg:h-screen gap-4 items-center;
    .shape-container {
      @apply w-full lg:w-[90%] relative lg:absolute top-0 z-10 overflow-hidden;
      left: 50%;
      transform: translate(-50%, 0);
      @include tablet-portrait {
        left: 0;
        transform: translate(0, 0);
      }
      @include mobile {
        left: 0;
        transform: translate(0, 0);
      }
      &.active {
        @apply z-1;
      }
      #hexagon-shape {
        @apply py-64 lg:py-0;
        transform: scale(1) translate(0, 0);
        @include tablet-portrait {
          transform: scale(1.6) translate(8px, 0);
        }
        @include mobile {
          transform: scale(1.8) translate(8px, 0);
        }
      }
    }
    .row-svg {
      @apply w-full lg:w-7/12 h-full;
    }
    .row {
      @apply w-full lg:w-[44%] max-w-[460px] left-0 relative z-1 pt-0 lg:pt-20 h-full;
      .top-area {
        @apply flex justify-between;
        .close-button {
          @apply flex justify-end mb-12;
          img {
            @apply w-20 h-20 transition-all ease-in-out cursor-pointer;
            transform: rotate(0deg) scale(1);
            &:hover {
              transform: rotate(90deg) scale(1.2);
            }
          }
        }
        .icon {
          @apply flex;
          img {
            @apply w-20 h-20 transition-all ease-in-out cursor-pointer;
            transform: rotate(180deg) scale(1);
            &:hover {
              transform: rotate(180deg) scale(1.2);
            }
          }
        }
      }
      &.active {
        @apply z-10;
      }
    }
  }

  .slide-fade-enter-active {
    transition: all 0.3s ease-in-out;
  }

  .slide-fade-leave-active {
    transition: all 0.3s ease-in-out;
  }

  .slide-fade-enter-from,
  .slide-fade-leave-to {
    transform: translateX(100px);
    opacity: 0;
  }
}
</style>
